import {
  BASIC_HEALTH_INFO,
  BULK_UPLOAD,
  CAMP_DASHBOARD_STATS,
  CAMP_REPORT,
  CAMP_VISIT_LINK,
  CREAT_CAMP_TYPE,
  CREATE_CAMP,
  CREATE_CAMP_LAB_ORDER,
  CREATE_CAMP_PATIENT,
  CREATE_CAMP_PATIENT_VITALS,
  CREATE_CHIEF_COMPLAINTS,
  CREATE_ENT_SCREENING,
  CREATE_MEDICAL_HISTORY,
  CREATE_OPHTHAMOLOGY_SCREENING,
  DELETE_DOCUMENT,
  DOWNLOAD_LAB__ORDERS_EXCEL,
  GET_CAMP_APPOINTMENTS,
  GET_CAMP_CLINICAL_LIST,
  GET_CAMP_LAB_ORDERS,
  GET_CAMP_PATIENT_VITALS,
  GET_CAMP_TYPE,
  GET_DOC_COMMETS,
  GET_DOCTORS,
  GET_ECG_SCREENING,
  GET_ENT_SCREENING,
  GET_FOLLOW_UP_ACTIONS,
  GET_LAB_PACKAGES,
  GET_OPHTHAMOLOGY_SCREENING,
  GET_PATIENT_GRADE,
  GET_PATIENTS_LIST,
  GET_PFT_SCREENING,
  GET_SPECIALIZATION,
  PATIENT_LIST_CAMP,
  SEND_CAMP_WHATSAPP_MESSAGE,
  UPDATE_CAMP_LAB_ORDER,
  UPDATE_CAMP_PATIENT,
  UPDATE_CAMP_STATUS,
  UPDATE_CAMP_VISIT_DOCTOR,
  UPDATE_LAB_ORDER_BARCODE,
  UPDATE_PATIENT_GRADE,
  UPLOAD_ALL_DOCUMENTS,
  UPLOAD_DOCUMENT,
  UPLOAD_ECG_SCREENING,
  UPLOAD_PDF_TO_BUCKET,
  UPLOAD_PFT_SCREENING,
  RESEND_NOTIFICATION,
} from '../utils/Endpoints'
import HttpService from '../utils/httpService'
import { TransformConstants } from '../features/opdPatientView/Constants'

// Get the configured axios client with token refresh interceptor
// This automatically handles token refresh before each request
const axiosClient = HttpService.getAxiosClient()

// EXPLANATION OF THE FIX:
// ========================
// Previously, this file was using plain `axios` and manually calling `KeycloakService.getToken()`
// This caused 401 errors because:
// 1. Tokens expire after a certain time
// 2. Manual token retrieval doesn't refresh expired tokens
// 3. No retry logic when token expires mid-session
//
// The Fix:
// --------
// Now using `axiosClient` from `HttpService.getAxiosClient()` which has interceptors that:
// - Automatically check if user is logged in
// - Call `KeycloakService.updateToken()` before EACH request
// - Refresh the token if it's expired or about to expire
// - Add the fresh token to request headers automatically
//
// This ensures all API calls always use a valid, fresh token.

// Utility function to transform FHIR Bundle data to simple patient array
export const transformFHIRPatientData = (fhirBundle: any): any[] => {
  if (!fhirBundle?.entry || !Array.isArray(fhirBundle.entry)) {
    return []
  }

  return fhirBundle?.entry
    ?.map((entry: any) => {
      const patient = entry?.resource
      if (!patient || patient?.resourceType !== TransformConstants?.PATIENT) {
        return null
      }

      // Extract identifiers
      const patientIdentifier = patient?.identifier?.find(
        (id: any) => id.system === TransformConstants?.PATIENT_IDENTIFIER
      )
      const thopIdentifier = patient?.identifier?.find(
        (id: any) => id.system === TransformConstants?.THOP_IDENTIFIER
      )
      const phoneIdentifier = patient?.telecom?.find(
        (tel: any) => tel.system === TransformConstants?.PHONE
      )

      // Extract name
      const name = patient.name?.[0]
      const givenNames = name?.given || []
      const familyName = name?.family || ''
      const fullName = `${givenNames.join(' ')} ${familyName}`.trim()

      // Extract address
      const address = patient.address?.[0]
      const addressLines = address?.line || []
      const city = address?.city || ''
      const state = address?.state || ''
      const fullAddress = [...addressLines, city, state]
        .filter(Boolean)
        .join(', ')

      // Calculate age from birthDate
      const calculateAge = (birthDate: string): number => {
        if (!birthDate) return 0
        const birth = new Date(birthDate)
        const today = new Date()
        let age = today?.getFullYear() - birth?.getFullYear()
        const monthDiff = today?.getMonth() - birth?.getMonth()
        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today?.getDate() < birth?.getDate())
        ) {
          age--
        }
        return age
      }

      return {
        id: patient?.id,
        uhid: patientIdentifier?.value || thopIdentifier?.value || '',
        name: fullName,
        phoneNo: phoneIdentifier?.value || '',
        age: patient?.birthDate ? calculateAge(patient?.birthDate) : 0,
        gender: patient?.gender || '',
        address: fullAddress,
        birthDate: patient?.birthDate || '',
        registrationType:
          patient?.identifier?.find(
            (id: any) => id?.system === TransformConstants?.REGISTRATION_TYPE
          )?.value || '',
        fhirData: patient,
      }
    })
    .filter(Boolean)
}

// Note: All functions below now use axiosClient instead of axios
// No need to manually get token or add Authorization header
// The interceptor handles everything automatically

export const createCampType = async (data: any) => {
  try {
    const response: any = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREAT_CAMP_TYPE}`,
      data
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const getCampTypes = async () => {
  try {
    const response: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_CAMP_TYPE}`
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const createCamp = async (data: any) => {
  try {
    const response: any = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_CAMP}`,
      data
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const updateCamp = async (data: any) => {
  try {
    const response: any = await axiosClient.put(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_CAMP}`,
      data
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

/**
 * Fetch active camps with pagination.
 * Parameters split on separate lines to satisfy Prettier formatting rules.
 * pageNo and size have defaults for backward compatibility.
 */
export const getCampsList = async (
  startDate: any,
  endDate: any,
  pageNo: any = 0,
  size: any = 10
) => {
  try {
    const response: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_CAMP}?fromDate=${startDate}&toDate=${endDate}&page=${pageNo}&sortBy=fromDate&sortOrder=asc&size=${size}`
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

/**
 * Fetch upcoming camps with pagination.
 */
export const getUpcomingCampsList = async (
  startDate: any,
  pageNo: any = 0,
  size: any = 10
) => {
  try {
    const response: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_CAMP}?fromDate=${startDate}&page=${pageNo}&sortBy=fromDate&sortOrder=asc&size=${size}`
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

/**
 * Fetch completed camps with pagination.
 */
export const getCompletedCampsList = async (
  endDate: any,
  pageNo: any = 0,
  size: any = 10
) => {
  try {
    const response: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_CAMP}?toDate=${endDate}&page=${pageNo}&sortBy=fromDate&sortOrder=asc&size=${size}`
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const createCampPatient = async (data: any) => {
  try {
    const response: any = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_CAMP_PATIENT}`,
      data
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const updateCampPatientApi = async (data: any) => {
  try {
    const response: any = await axiosClient.put(
      `${process.env.REACT_APP_API_GATEWAY_URL}${UPDATE_CAMP_PATIENT}`,
      data
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const getCampPatientsList = async (
  campId: any,
  page: number,
  size: number,
  searchTerm?: {
    query: string
    param: string
  }
) => {
  try {
    let url = `${process.env.REACT_APP_API_GATEWAY_URL}${PATIENT_LIST_CAMP}?campId=${campId}&page=${page}&size=${size}`

    if (searchTerm?.query) {
      url += `&${searchTerm?.param}=${searchTerm?.query}`
    }

    const response: any = await axiosClient.get(url)
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

// Continuing with all remaining functions...
// All follow the same pattern: use axiosClient instead of axios + manual token

export const getCommentsByCampId = async (visitId: string) => {
  try {
    const response: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_DOC_COMMETS}/${visitId}`
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const postCommentsByCampId = async (data: any) => {
  try {
    const response: any = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_DOC_COMMETS}`,
      data
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const updateCommentsByCampId = async (data: any) => {
  try {
    const response: any = await axiosClient.put(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_DOC_COMMETS}`,
      data
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const campVisitCreate = async (data: any) => {
  try {
    const response: any = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CAMP_VISIT_LINK}`,
      data
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const getCampReportById = async (campId: string) => {
  try {
    const response: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CAMP_REPORT}/${campId}`
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const getSpecialization = async () => {
  try {
    const response: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_SPECIALIZATION}`
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const getDoctors = async (specialization: string) => {
  try {
    const response: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_DOCTORS}?speciality=${specialization}`
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const getFollowUpActions = async () => {
  try {
    const response: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_FOLLOW_UP_ACTIONS}`
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const getAllCampAppointments = async (
  campId: string,
  patientUhid: string
) => {
  try {
    const response: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_CAMP_APPOINTMENTS}?campId=${campId}&patientUhid=${patientUhid}`
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const addBaiscHealthInfo = async (data: any) => {
  try {
    const response: any = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${BASIC_HEALTH_INFO}`,
      data
    )
    if (response?.data?.success) {
      const { data, message, success } = response?.data ?? {}
      return {
        data,
        message,
        success,
      }
    } else {
      return {
        success: false,
        data: [],
        message: response?.data?.message,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: [],
      message: error?.response?.data?.message,
    }
  }
}

export const getLabPackages = async (): Promise<[]> => {
  try {
    const response = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_LAB_PACKAGES}`
    )

    return response?.data?.packages || []
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to fetch lab packages'
    throw new Error(errorMessage)
  }
}

export const getPatientsList = async (phoneNo: string): Promise<any[]> => {
  try {
    const response = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_PATIENTS_LIST}=${phoneNo}&_count=50`
    )
    const transformedData = transformFHIRPatientData(response?.data)
    return transformedData
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to fetch patients list'
    throw new Error(errorMessage)
  }
}

export const updateCampStatus = async (visitId: string, status: string) => {
  try {
    const response = await axiosClient.put(
      `${process.env.REACT_APP_API_GATEWAY_URL}${UPDATE_CAMP_STATUS}?visitId=${visitId}&status=${status}`,
      null, // No request body
      {
        maxRedirects: 0, // Prevent automatic redirect following
        validateStatus: (status) => status >= 200 && status < 400, // Accept 2xx and 3xx
      }
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Camp status updated successfully',
      success: true,
    }
  } catch (error: any) {
    // Handle 302 redirect
    if (error.response?.status === 302) {
      // Consider the operation successful if we get a redirect
      return {
        success: true,
        data: null,
        message: 'Operation completed with redirect',
      }
    }

    // Handle other errors
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to update camp status'
    throw new Error(errorMessage)
  }
}

export const createChiefComplaints = async (data: any) => {
  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_CHIEF_COMPLAINTS}`,
      data
    )

    return {
      data: response.data?.data,
      message:
        response.data?.message || 'Chief complaints created successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to create chief complaints'
    throw new Error(errorMessage)
  }
}

export const updateChiefComplaints = async (data: any, chiefId: string) => {
  try {
    const response = await axiosClient.put(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_CHIEF_COMPLAINTS}/${chiefId}`,
      data
    )

    return {
      data: response.data?.data,
      message:
        response.data?.message || 'Chief complaints updated successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to update chief complaints'
    throw new Error(errorMessage)
  }
}

export const getChiefComplaints = async (visitId: string) => {
  try {
    const response = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_CHIEF_COMPLAINTS}/visit/${visitId}`
    )

    return {
      data: response.data?.data,
      message:
        response.data?.message || 'Chief complaints fetched successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to fetch chief complaints'
    throw new Error(errorMessage)
  }
}

export const getCampMedicalHistory = async (visitId: string) => {
  try {
    const response = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_MEDICAL_HISTORY}/visit/${visitId}`
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Medical history fetched successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to fetch medical history'
    throw new Error(errorMessage)
  }
}

export const createMedicalHistory = async (data: any) => {
  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_MEDICAL_HISTORY}`,
      data
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Medical history created successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to create medical history'
    throw new Error(errorMessage)
  }
}

export const updateMedicalHistory = async (data: any, medicalId: string) => {
  try {
    const response = await axiosClient.put(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_MEDICAL_HISTORY}/${medicalId}`,
      data
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Medical history updated successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to update medical history'
    throw new Error(errorMessage)
  }
}

export const getAllDocuments = async (visitId: any) => {
  try {
    const response = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${UPLOAD_ALL_DOCUMENTS}/visit/${visitId}`
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Documents fetched successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to fetch documents'
    throw new Error(errorMessage)
  }
}

export const uploadAllDocuments = async (data: any) => {
  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${UPLOAD_ALL_DOCUMENTS}`,
      data
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Document uploaded successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to upload document'
    throw new Error(errorMessage)
  }
}

export const updateUploadAllDocuments = async (
  data: any,
  documentID: string
) => {
  try {
    const response = await axiosClient.put(
      `${process.env.REACT_APP_API_GATEWAY_URL}${UPLOAD_ALL_DOCUMENTS}/${documentID}`,
      data
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Document updated successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to update document'
    throw new Error(errorMessage)
  }
}

export const uploadDocument = async (
  file: File,
  options: {
    patientUhid?: string
    campId?: string
    visitId?: string
    testTypeLabel: string
    index: number
  }
) => {
  const formData = new FormData()
  formData.append('file', file)

  if (options?.patientUhid) {
    formData.append('patientUhid', options.patientUhid)
  }
  if (options?.campId) {
    formData.append('campId', options.campId)
  }
  if (options?.visitId) {
    formData.append('visitId', options.visitId)
  }
  formData.append('testTypeLabel', options.testTypeLabel)
  formData.append('index', options.index.toString())

  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${UPLOAD_DOCUMENT}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )

    if (response?.data?.success) {
      return {
        success: true,
        data: response?.data?.data,
        message: response?.data?.message,
      }
    } else {
      throw new Error(response?.data?.message)
    }
  } catch (error: any) {
    console.error('Error uploading document:', error)
    throw new Error(
      error?.response?.data?.message || 'Error uploading document'
    )
  }
}

export const bulkUpload = async (file: any) => {
  const formData = new FormData()
  formData.append('file', file)

  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${BULK_UPLOAD}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Bulk upload successful',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to perform bulk upload'
    throw new Error(errorMessage)
  }
}

export const removeDocument = async (fileName: string) => {
  try {
    const response = await axiosClient.delete(
      `${process.env.REACT_APP_API_GATEWAY_URL}${DELETE_DOCUMENT}?fileName=${fileName}`
    )

    return {
      data: response?.data,
      message: response?.data?.message || 'Document removed successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to remove document'
    throw new Error(errorMessage)
  }
}

export const createCampPatientVitals = async (data: any) => {
  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_CAMP_PATIENT_VITALS}`,
      data
    )

    return {
      data: response.data?.data,
      message:
        response.data?.message || 'Camp patient vitals created successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to create camp patient vitals'
    throw new Error(errorMessage)
  }
}

export const updateCampPatientVitals = async (data: any) => {
  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_CAMP_PATIENT_VITALS}`,
      data
    )

    return {
      data: response.data?.data,
      message:
        response.data?.message || 'Camp patient vitals updated successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to update camp patient vitals'
    throw new Error(errorMessage)
  }
}

export const getCampPatientVitals = async (visitId: string) => {
  try {
    const response = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_CAMP_PATIENT_VITALS}${visitId}`
    )

    return {
      data: response.data?.data,
      message:
        response.data?.message || 'Camp patient vitals fetched successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to fetch camp patient vitals'
    throw new Error(errorMessage)
  }
}

export const createCampLabOrder = async (data: any) => {
  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_CAMP_LAB_ORDER}`,
      data
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Lab order created successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to create lab order'
    throw new Error(errorMessage)
  }
}

export const UpdateLaborderAPi = async (
  body: any,
  phoneNumber: any,
  patientName: any
) => {
  try {
    const response = await axiosClient.put(
      `${process.env.REACT_APP_API_GATEWAY_URL}${UPDATE_CAMP_LAB_ORDER(
        phoneNumber,
        patientName
      )}`,
      body
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Lab order Updated successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to updated lab order'
    throw new Error(errorMessage)
  }
}

export const GetCampLabOrdersApi = async (campsId: any, patientUhid: any) => {
  try {
    const response = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_CAMP_LAB_ORDERS(
        campsId,
        patientUhid
      )}`
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Lab order fetched successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to Get lab order'
    throw new Error(errorMessage)
  }
}

export const UpdateCampVisitDoctorApi = async (body: any) => {
  try {
    const response = await axiosClient.patch(
      `${process.env.REACT_APP_API_GATEWAY_URL}${UPDATE_CAMP_VISIT_DOCTOR}`,
      body
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Updated Doctor successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to Update Doctor'
    throw new Error(errorMessage)
  }
}

export const UploadEcgScreeningApi = async (body: any) => {
  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${UPLOAD_ECG_SCREENING}`,
      body
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Upload ECG screening successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to upload ECG screening'
    throw new Error(errorMessage)
  }
}

export const GetEcgScreeningApi = async (
  campsId: any,
  patientUhid: any,
  visitId: any
) => {
  try {
    const response = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_ECG_SCREENING(
        campsId,
        patientUhid,
        visitId
      )}`
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'ECG screening fetched successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to fetch ECG screening'
    throw new Error(errorMessage)
  }
}

export const UploadPftScreeningApi = async (body: any) => {
  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${UPLOAD_PFT_SCREENING}`,
      body
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'PFT screening uploaded successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to upload PFT screening'
    throw new Error(errorMessage)
  }
}

export const SendReportWhatsappMessageApi = async (body: any) => {
  try {
    const response: any = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${SEND_CAMP_WHATSAPP_MESSAGE}`,
      body
    )

    return {
      data: response.data?.data,
      message:
        response.data?.message ??
        response?.message ??
        'Whatsapp message sent successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to send Whatsapp message'
    throw new Error(errorMessage)
  }
}

export const GetPftScreeningApi = async (
  campsId: any,
  patientUhid: any,
  visitId: any
) => {
  try {
    const response = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_PFT_SCREENING(
        campsId,
        patientUhid,
        visitId
      )}`
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'PFT screening fetched successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to fetch PFT screening'
    throw new Error(errorMessage)
  }
}

export const UploadPdfToBucketApi = async (body: any) => {
  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${UPLOAD_PDF_TO_BUCKET}`,
      body,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Updated Doctor successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to Update Doctor'
    throw new Error(errorMessage)
  }
}

export const getBucktFileApi = async (url: string) => {
  try {
    const response: any = await axiosClient.get(url, {
      headers: {
        // spread inside headers
        Accept: 'application/pdf',
      },
      responseType: 'arraybuffer', // fetch raw PDF bytes
    })
    if (response?.data) {
      return {
        success: true,
        data: response?.data,
        message: 'File fetched Successfully',
      }
    } else {
      return {
        success: false,
        data: null,
        message: 'Failed to fetch file',
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: null,
      message: error?.response?.data?.message || error?.message,
    }
  }
}

export const CreateOththamologyApi = async (
  body: any,
  visitId: any,
  patientId: any,
  practitionerId: any
) => {
  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_OPHTHAMOLOGY_SCREENING(
        visitId,
        patientId,
        practitionerId
      )}`,
      body
    )
    if (response?.data) {
      return {
        success: true,
        data: response?.data,
        message: 'File fetched Successfully',
      }
    } else {
      return {
        success: false,
        data: null,
        message: 'Failed to fetch file',
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: null,
      message: error?.response?.data?.message || error?.message,
    }
  }
}

export const GetClinicalDataListApi = async (body: any) => {
  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_CAMP_CLINICAL_LIST}`,
      body
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Updated Doctor successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to Update Doctor'
    throw new Error(errorMessage)
  }
}

export const CreateENTApi = async (
  body: any,
  visitId: any,
  patientId: any,
  practitionerId: any
) => {
  try {
    const response = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CREATE_ENT_SCREENING(
        visitId,
        patientId,
        practitionerId
      )}`,
      body
    )
    if (response?.data) {
      return {
        success: true,
        data: response?.data,
        message: 'Create ENT SCreening Successfully',
      }
    } else {
      return {
        success: false,
        data: null,

        message: 'Failed to Create ENT SCreening',
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: null,
      message:
        error?.response?.data?.message ||
        error?.message ||
        'Failed to download lab orders',
    }
  }
}

export const GetOththamologyApi = async (visitId: any) => {
  try {
    const response = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_OPHTHAMOLOGY_SCREENING(
        visitId
      )}`
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Updated Doctor successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to Update Doctor'
    throw new Error(errorMessage)
  }
}

export const GetENTApi = async (visitId: any) => {
  try {
    const response = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_ENT_SCREENING(visitId)}`
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Updated Doctor successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to Update Doctor'
    throw new Error(errorMessage)
  }
}

export const UpdateLabOrderBarcodeApi = async (body: any) => {
  try {
    const response = await axiosClient.patch(
      `${process.env.REACT_APP_API_GATEWAY_URL}${UPDATE_LAB_ORDER_BARCODE}`,
      body
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Updated Doctor successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to Update Doctor'
    throw new Error(errorMessage)
  }
}

export const downloadCampLabOrders = async (
  campId: string,
  formDate: string,
  toDate: string
) => {
  try {
    const response: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${DOWNLOAD_LAB__ORDERS_EXCEL(
        campId,
        formDate,
        toDate
      )}`,
      {
        headers: {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob', // fetch as blob for Excel file
      }
    )
    if (response?.data) {
      // Create a download link for the Excel file
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `Lab_Orders_${campId}_${formDate}_to_${toDate}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      return {
        success: true,
        data: response?.data,
        message: 'Lab orders downloaded successfully',
      }
    } else {
      return {
        success: false,
        data: null,
        message: 'Failed to download lab orders',
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: null,
      message:
        error?.response?.data?.message ||
        error?.message ||
        'Failed to download lab orders',
    }
  }
}

export const getCampDashboardStats = async (
  campId: string,
  fromDate: string,
  toDate: string
) => {
  try {
    const response: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${CAMP_DASHBOARD_STATS}${campId}?startDate=${fromDate}&endDate=${toDate}`
    )
    if (response?.data) {
      return {
        success: true,
        data: response?.data,
        message:
          response?.data?.message ||
          'Camp dashboard stats fetched successfully',
      }
    } else {
      return {
        success: false,
        data: null,
        message: 'Failed to fetch camp dashboard stats',
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: null,
      message: error?.response?.data?.message || error?.message,
    }
  }
}

export const UpdatePatientGradeApi = async (body: any) => {
  try {
    const response = await axiosClient.patch(
      `${process.env.REACT_APP_API_GATEWAY_URL}${UPDATE_PATIENT_GRADE}`,
      body
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Updated Patient Grade successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to Update Patient Grade'
    throw new Error(errorMessage)
  }
}
export const GetPatientGradeApi = async (muhId: any, campId: any) => {
  try {
    const response = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_PATIENT_GRADE(
        muhId,
        campId
      )}`
    )

    return {
      data: response.data?.data,
      message: response.data?.message || 'Updated Patient Grade successfully',
      success: true,
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to Update Patient Grade'
    throw new Error(errorMessage)
  }
}

export const resendNotification = async (data: {
  patientUhid: string
  patientPhoneNumber: string
  patientName: string
  location: string
  hospital: string
  barcode?: string
  notificationType: 'REGISTRATION' | 'LAB'
  messageType: 'SMS' | 'WHATSAPP'
}) => {
  try {
    const response: any = await axiosClient.post(
      `${process.env.REACT_APP_API_GATEWAY_URL}${RESEND_NOTIFICATION}`,
      data
    )
    if (response?.data?.success) {
      const { data: resData, message, success } = response?.data ?? {}
      return { data: resData, message, success }
    } else {
      return {
        success: false,
        data: null,
        message: response?.data?.message || 'Failed to resend notification',
      }
    }
  } catch (error: any) {
    return {
      success: false,
      data: null,
      message:
        error?.response?.data?.message || 'Failed to resend notification',
    }
  }
}
